import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Button, EmptyState, Spinner } from "./components/ui";
import { useAuth } from "./lib/auth";
import { currentSurface } from "./lib/host";
import { useDocumentTitle } from "./lib/useDocumentTitle";
import { LoginPage } from "./routes/Login";
import { VenueLayout } from "./routes/venue/Layout";
import { OperatorLayout } from "./routes/operator/Layout";

// Each surface is its own chunk: door staff never download the panel, the
// panel never downloads the camera decoder.
const ClaimPage = lazy(() => import("./routes/Claim").then((m) => ({ default: m.ClaimPage })));
const DoorScanner = lazy(() => import("./routes/DoorScanner").then((m) => ({ default: m.DoorScanner })));
const RewardScanner = lazy(() => import("./routes/RewardScanner").then((m) => ({ default: m.RewardScanner })));
const PassesPage = lazy(() => import("./routes/venue/Passes").then((m) => ({ default: m.PassesPage })));
const PassDetailPage = lazy(() => import("./routes/venue/PassDetail").then((m) => ({ default: m.PassDetailPage })));
const VenueStatsPage = lazy(() => import("./routes/venue/Stats").then((m) => ({ default: m.VenueStatsPage })));
const VenueSettingsPage = lazy(() => import("./routes/venue/Settings").then((m) => ({ default: m.VenueSettingsPage })));
const VenueTiersPage = lazy(() => import("./routes/venue/Tiers").then((m) => ({ default: m.VenueTiersPage })));
const QrSheetPage = lazy(() => import("./routes/venue/QrSheet").then((m) => ({ default: m.QrSheetPage })));
const VenuesPage = lazy(() => import("./routes/operator/Venues").then((m) => ({ default: m.VenuesPage })));
const VenueDetailPage = lazy(() => import("./routes/operator/VenueDetail").then((m) => ({ default: m.VenueDetailPage })));
const PlatformStatsPage = lazy(() => import("./routes/operator/PlatformStats").then((m) => ({ default: m.PlatformStatsPage })));
const OperatorSettingsPage = lazy(() => import("./routes/operator/Settings").then((m) => ({ default: m.OperatorSettingsPage })));
/**
 * Route table.
 *
 * The claim pages and the scanners live outside the authenticated shell: a
 * winner is not a user of the system, and door staff authenticate with a PIN
 * rather than a session.
 */
export function App() {
  const surface = currentSurface();
  useDocumentTitle();

  return (
    <ErrorBoundary>
    <Suspense fallback={<Spinner />}>
      <Routes>
      {/* Public: the winner's page, reached from a QR code. */}
      <Route path="/claim/:token" element={<ClaimPage />} />

      {/* Staff surfaces, PIN-gated inside the component. */}
      <Route path="/scanner" element={<DoorScanner />} />
      <Route path="/rewards" element={<RewardScanner />} />

      {surface === "operator" ? (
        <Route path="/*" element={<OperatorRoutes />} />
      ) : (
        <Route path="/*" element={<VenueRoutes />} />
      )}
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

/**
 * The session check failed for a reason other than "no session": the API is
 * down or the proxy is broken. A login form here would fail too, and would
 * tell the venue it was logged out when it was not.
 */
function Unreachable({ onRetry }: { onRetry: () => void }) {
  return (
    <main className="flex min-h-full items-center justify-center px-5 py-12">
      <EmptyState
        variant="error"
        title="Brak połączenia z serwerem"
        hint="Panel spróbuje ponownie sam po powrocie do karty. Możesz też odświeżyć stronę."
        action={
          <Button size="sm" onClick={onRetry}>
            Spróbuj ponownie
          </Button>
        }
        className="w-full max-w-[420px]"
      />
    </main>
  );
}

function VenueRoutes() {
  const { user, loading, unreachable, retry } = useAuth();

  if (loading) return <Spinner />;
  if (unreachable) return <Unreachable onRetry={retry} />;
  if (!user) return <LoginPage />;

  return (
    <Routes>
      {/* The print sheet renders outside the panel chrome: paper wants no nav. */}
      <Route path="print/qr-sheet" element={<QrSheetPage />} />
      <Route element={<VenueLayout />}>
        <Route index element={<PassesPage />} />
        <Route path="passes/:serial" element={<PassDetailPage />} />
        <Route path="tiers" element={<VenueTiersPage />} />
        <Route path="stats" element={<VenueStatsPage />} />
        <Route path="settings" element={<VenueSettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function OperatorRoutes() {
  const { user, loading, unreachable, retry } = useAuth();

  if (loading) return <Spinner />;
  if (unreachable) return <Unreachable onRetry={retry} />;
  if (!user) return <LoginPage />;

  return (
    <Routes>
      <Route element={<OperatorLayout />}>
        <Route index element={<VenuesPage />} />
        <Route path="venues/:id/:tab?" element={<VenueDetailPage />} />
        <Route path="stats" element={<PlatformStatsPage />} />
        <Route path="settings" element={<OperatorSettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
